import ContactHeader from '@/components/contact-header/contact-header';
import { getEntityColor } from '@/components/entity-colors';
import CreateButton from '@/components/entity-view/create-button';
import EntityHeader from '@/components/entity-view/entity-header';
import { Button } from '@/components/ui/button';
import { getEntityQueryOptions, type GetEntityType } from '@/queries/getEntityQueryOptions';
import { moduleDefinitionsQueryOptions } from '@/queries/moduleDefinitionsQueryOptions';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Separator } from '@radix-ui/react-separator';
import { useQuery } from '@tanstack/react-query';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { createFileRoute, Outlet, useNavigate, useBlocker } from '@tanstack/react-router'
import { EllipsisVerticalIcon, PencilIcon, SaveIcon, CheckIcon, LoaderIcon } from 'lucide-react';
import { toast } from 'sonner';
import { sidebarsQueryOptions } from '@/queries/sidebarsQueryOptions';
import OverviewPanel from '@/components/activity/overview-panel';
import { useState, useEffect } from 'react';
import React from 'react';
export const SubEntityRegistrationContext = React.createContext<{
    registerSubEntityForm: (subEntityId: string, formInstance: any) => void;
    unregisterSubEntityForm: (subEntityId: string) => void;
    storeSubEntityChanges: (subEntityId: string, values: any) => void;
    justSaved: React.MutableRefObject<boolean>;
    updateInitialValues: (subEntityId: string, newValues: any) => void;
    registerUpdateCallback: (subEntityId: string, callback: (newValues: any) => void) => void;
} | null>(null);
import HouseholdMembersPanel from '@/components/household-members/household-members-panel';
import { MergeContactsModal } from '@/components/contacts/merge-contacts-modal';
import { CommandDialog, CommandEmpty, CommandInput, CommandList } from "@/components/ui/command"
import { useCommandStore } from "@/stores/command-store"
import { entityConditionsQueryOptions } from '@/queries/entityConditionsQueryOptions';
import EntityCreateCommands from '@/components/entity-view/entity-create-commands';
import RecommendedActions from '@/components/entity-view/recommended-actions';
import EntityBreadcrumbs from '@/components/entity-view/entity-breadcrumbs';
import EntityActionsDropdown from '@/components/entity-view/entity-actions-dropdown';
import LoadingSpinner from '@/components/spinner';
import { useAppForm, appFormContext } from '@/hooks/use-form';
import { useStore } from '@tanstack/react-form';
import { updateEntity } from '@/queries/updateEntityMutation';
import { updateSubEntity } from '@/queries/updateSubEntityMutation';

export const Route = createFileRoute(
    '/_app/$module_id/$entity_type_id/$entity_id',
)({
    component: RouteComponent,
    loader: async ({ context: { queryClient }, params }) => {
        const { entity_type_id, entity_id } = params;
        await queryClient.ensureQueryData(moduleDefinitionsQueryOptions);
        await queryClient.ensureQueryData(getEntityQueryOptions(entity_type_id, entity_id));
        await queryClient.ensureQueryData(sidebarsQueryOptions(entity_type_id));
    }
});

function RouteComponent() {
    const { entity_type_id, entity_id } = Route.useParams();


    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: result, isLoading } = useQuery(getEntityQueryOptions(entity_type_id, entity_id));

    if (isLoading) {
        return <LoadingSpinner />
    }

    if (!result) {
        return <div>Error</div>
    }

    return <InnerEntityComponent result={result} />

}

function InnerEntityComponent({ result }: { result: GetEntityType<any> }) {

    const currentEntity = result?.entity;

    const { module_id, entity_type_id, entity_id } = Route.useParams();
    const { visible, setVisible } = useCommandStore();
    const navigate = useNavigate();
    const moduleDefinitions = useQuery(moduleDefinitionsQueryOptions);
    const moduleDefinition = moduleDefinitions.data?.find(m => m.id === module_id);
    // Query for sub-collections based on entity type
    const { data: conditions } = useQuery(entityConditionsQueryOptions(entity_type_id));
    const subCollections = conditions?.filter(c => c.displayType === 'collection') || [];

    useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                setVisible(true);
            }

            if (e.key === "Escape") {
                setVisible(false);
            }
        }

        document.addEventListener("keydown", down)
        return () => document.removeEventListener("keydown", down)
    }, [setVisible])

    const updateEntityMutation = updateEntity();
    const updateSubEntityMutation = updateSubEntity();
    
    // Define sub-entity collections to exclude from main entity updates
    const SUB_ENTITY_COLLECTIONS = ['phones', 'emailAddresses', 'addresses', 'householdMembers'];
    
    // Helper function to find sub-entity by ID across all sub-entity collections
    const findSubEntityById = (entity: any, subEntityId: string) => {
        if (!entity) return null;
        
        for (const collectionName of SUB_ENTITY_COLLECTIONS) {
            const collection = entity[collectionName];
            if (collection && Array.isArray(collection)) {
                const item = collection.find((item: any) => item.id === subEntityId);
                if (item) {
                    return {
                        type: collectionName === 'emailAddresses' ? 'email-addresses' : 
                              collectionName === 'householdMembers' ? 'household-members' :
                              collectionName,
                        originalData: item
                    };
                }
            }
        }
        return null;
    };
    
    // Helper function to get current form values for a sub-entity
    // This will store form instances instead of values to avoid stale data
    const subEntityFormRefs = React.useRef<Map<string, any>>(new Map());
    
    // Store changed values immediately when they occur to avoid losing them during re-renders
    const subEntityChanges = React.useRef<Map<string, any>>(new Map());
    
    // Track if we just completed a save to prevent immediately marking as dirty again
    const justSaved = React.useRef<boolean>(false);
    
    // Store references to update initial values in sub-entity forms
    const updateInitialValuesCallbacks = React.useRef<Map<string, (newValues: any) => void>>(new Map());
    
    const getSubEntityFormValues = (subEntityId: string) => {
        // First check if we have stored changes for this sub-entity
        if (subEntityChanges.current.has(subEntityId)) {
            const storedChanges = subEntityChanges.current.get(subEntityId);
            console.log(`Using stored changes for ${subEntityId}:`, storedChanges);
            return storedChanges;
        }
        
        // Fallback to live form instance if no stored changes
        const formInstance = subEntityFormRefs.current.get(subEntityId);
        if (formInstance) {
            // Get the current state directly from the form instance
            const currentState = formInstance.store.state.values;
            console.log(`Getting live form values for ${subEntityId}:`, currentState);
            return currentState;
        }
        console.log(`No form instance found for ${subEntityId}`);
        return null;
    };
    
    // Function to register sub-entity form instances
    const registerSubEntityForm = (subEntityId: string, formInstance: any) => {
        console.log(`Registering form instance for ${subEntityId}`);
        subEntityFormRefs.current.set(subEntityId, formInstance);
    };
    
    // Function to unregister sub-entity form instances
    const unregisterSubEntityForm = (subEntityId: string) => {
        console.log(`Unregistering form instance for ${subEntityId}`);
        subEntityFormRefs.current.delete(subEntityId);
        // DON'T clear stored changes when unregistering - we need to preserve them across re-renders
        // subEntityChanges.current.delete(subEntityId);
    };
    
    // Function to store sub-entity changes immediately when they occur
    const storeSubEntityChanges = (subEntityId: string, values: any) => {
        console.log(`Storing changes for ${subEntityId}:`, values);
        subEntityChanges.current.set(subEntityId, values);
    };
    
    // Function to register callback for updating initial values
    const registerUpdateCallback = (subEntityId: string, callback: (newValues: any) => void) => {
        console.log(`Registering update callback for ${subEntityId}`);
        updateInitialValuesCallbacks.current.set(subEntityId, callback);
    };
    
    // Function to update initial values in sub-entity forms after save
    const updateInitialValues = (subEntityId: string, newValues: any) => {
        const callback = updateInitialValuesCallbacks.current.get(subEntityId);
        if (callback) {
            console.log(`Updating initial values for ${subEntityId}:`, newValues);
            callback(newValues);
        }
    };
    
    // Initialize form first
    const form = useAppForm({
        defaultValues: currentEntity,
        onSubmit: async ({ value }) => {
            console.log('=== MAIN FORM ONSUBMIT TRIGGERED ===');
            console.log('Form value:', value);
            console.log('Stored sub-entity changes available:', subEntityChanges.current.size > 0);
            console.log('Number of stored changes:', subEntityChanges.current.size);
            console.log('Stored changes:', Array.from(subEntityChanges.current.entries()));
            
            // Collect main entity changes (excluding sub-entity dummy fields)
            const changedValues: Record<string, any> = {};
            const subEntityUpdates: Array<{
                subEntityId: string;
                subEntityType: string;
                changes: Record<string, any>;
            }> = [];
            
            for (const key in value) {
                if (Object.prototype.hasOwnProperty.call(value, key)) {
                    // Handle sub-entity dirty flags
                    if (key.startsWith('__subEntity_') && key.endsWith('_dirty')) {
                        // Extract sub-entity ID from the dummy field name
                        const subEntityId = key.replace('__subEntity_', '').replace('_dirty', '');
                        console.log(`=== PROCESSING SUB-ENTITY ${subEntityId} ===`);
                        
                        // Find the sub-entity in the current entity data
                        const subEntityData = findSubEntityById(currentEntity, subEntityId);
                        console.log('Found sub-entity data for ID', subEntityId, ':', subEntityData);
                        
                        if (subEntityData) {
                            // Get the current form values for this sub-entity
                            let subEntityFormValues = getSubEntityFormValues(subEntityId);
                            console.log('Sub-entity form values for ID', subEntityId, ':', subEntityFormValues);
                            
                            // If we don't have current values from registration, try to get them directly from the form state
                            if (!subEntityFormValues) {
                                console.log('No registered values found, trying to get current form state...');
                                // This is a fallback - we should have the values from registration
                                subEntityFormValues = null;
                            }
                            
                            if (subEntityFormValues) {
                                // Compare with original values to find changes
                                console.log('Original data:', subEntityData.originalData);
                                console.log('Current form values:', subEntityFormValues);
                                
                                const fieldChanges: Record<string, any> = {};
                                for (const [fieldName, newValue] of Object.entries(subEntityFormValues)) {
                                    const originalValue = subEntityData.originalData[fieldName];
                                    console.log(`Comparing ${fieldName}: original="${originalValue}" (${typeof originalValue}) vs new="${newValue}" (${typeof newValue})`);
                                    
                                    // Skip fields that haven't actually changed
                                    if (newValue === originalValue) {
                                        continue;
                                    }
                                    
                                    // Special handling for fields that might be display values vs IDs
                                    // If the field name suggests it's an ID field but the value doesn't look like a GUID
                                    if (fieldName.endsWith('Id') && newValue && typeof newValue === 'string' && !newValue.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
                                        console.log(`Warning: Field ${fieldName} appears to be an ID field but value "${newValue}" is not a GUID`);
                                        // Skip this field - it's likely a display value that shouldn't be sent
                                        continue;
                                    }
                                    
                                    console.log(`Field ${fieldName} changed from`, originalValue, 'to', newValue);
                                    fieldChanges[fieldName] = newValue;
                                }
                                
                                console.log('Sub-entity field changes for ID', subEntityId, ':', fieldChanges);
                                
                                if (Object.keys(fieldChanges).length > 0) {
                                    subEntityUpdates.push({
                                        subEntityId,
                                        subEntityType: subEntityData.type,
                                        changes: fieldChanges
                                    });
                                }
                            } else {
                                console.log('No form values found for sub-entity ID:', subEntityId);
                            }
                        } else {
                            console.log('No sub-entity data found for ID:', subEntityId);
                        }
                        continue; // Skip adding to main entity changes
                    }
                    
                    // Handle main entity field changes (but exclude sub-entity collections)
                    if (!SUB_ENTITY_COLLECTIONS.includes(key) && value[key] !== currentEntity?.[key]) {
                        changedValues[key] = value[key];
                    }
                }
            }
            
            console.log('Main entity changes:', changedValues);
            console.log('Sub-entity changes:', subEntityUpdates);
            console.log('Current form instances registered:');
            Array.from(subEntityFormRefs.current.entries()).forEach(([id, formInstance]) => {
                if (formInstance && formInstance.store) {
                    console.log(`  ${id}: current values =`, formInstance.store.state.values);
                } else {
                    console.log(`  ${id}: no valid form instance`);
                }
            });
            
            // Mark that we just saved BEFORE doing any updates to prevent immediate re-dirtying
            justSaved.current = true;
            
            // Save main entity changes if any
            if (Object.keys(changedValues).length > 0) {
                console.log('Saving main entity changes...', changedValues);
                console.log('Main entity change keys:', Object.keys(changedValues));
                await updateEntityMutation.mutateAsync({
                    entityType: entity_type_id,
                    entityId: entity_id,
                    updates: changedValues,
                });
                console.log('Main entity changes saved successfully');
            } else {
                console.log('No main entity changes to save');
            }
            
            // Save sub-entity changes if any
            if (subEntityUpdates.length > 0) {
                console.log('Saving sub-entity changes...');
                
                // Save all sub-entities first and collect server responses
                const savedSubEntities: Array<{ subEntityId: string; serverData: any }> = [];
                
                for (const subEntityUpdate of subEntityUpdates) {
                    console.log('Saving sub-entity:', subEntityUpdate);
                    const serverResponse = await updateSubEntityMutation.mutateAsync({
                        subEntityType: subEntityUpdate.subEntityType,
                        subEntityId: subEntityUpdate.subEntityId,
                        parentEntityType: entity_type_id,
                        parentEntityId: entity_id,
                        updates: subEntityUpdate.changes,
                    });
                    
                    console.log('Server response for sub-entity:', subEntityUpdate.subEntityId, serverResponse);
                    savedSubEntities.push({ subEntityId: subEntityUpdate.subEntityId, serverData: serverResponse });
                    
                    // Clear stored changes for this sub-entity since it's now saved
                    subEntityChanges.current.delete(subEntityUpdate.subEntityId);
                }
                
                console.log('All sub-entity changes saved successfully');
                
                // Now update initial values for all saved sub-entities in a batch
                console.log('Batch updating initial values for all saved sub-entities...');
                for (const savedEntity of savedSubEntities) {
                    // Use the server response data which includes all fields including updateDate
                    console.log(`Batch updating initial values for ${savedEntity.subEntityId} with server data:`, savedEntity.serverData);
                    updateInitialValues(savedEntity.subEntityId, savedEntity.serverData);
                }
                console.log('Completed batch update of initial values');
            } else {
                console.log('No sub-entity changes to save');
            }
            
            console.log('Form submission completed');
            
            // Clear stored changes after successful submission
            subEntityChanges.current.clear();
            console.log('Cleared stored sub-entity changes after successful submission');
            
            // Force reset the form to clear dirty state and remove dummy fields
            const currentValues = form.store.state.values;
            const cleanValues = { ...currentValues };
            
            // Remove all sub-entity dirty flags
            Object.keys(cleanValues).forEach(key => {
                if (key.startsWith('__subEntity_') && key.endsWith('_dirty')) {
                    delete cleanValues[key];
                }
            });
            
            console.log('Force resetting form after submission to clear dirty state. Values before reset:', currentValues);
            console.log('Clean values for reset:', cleanValues);
            form.reset(cleanValues);
            console.log('Form reset completed. New form state:', form.store.state);
            
            // Reset the justSaved flag after a longer delay to ensure all sub-entity components have processed
            setTimeout(() => {
                justSaved.current = false;
                console.log('Reset justSaved flag - form can be marked dirty again');
                
                // Log the form state after everything should be settled
                console.log('Final form state after save:', {
                    isDirty: form.store.state.isDirty,
                    subEntityFields: Object.keys(form.store.state.values).filter(k => k.startsWith('__subEntity_')),
                    values: form.store.state.values
                });
            }, 3000); // Increased timeout to 3 seconds to cover query invalidation delay
        }
    });

    // Add debugging for parent form state changes
    useEffect(() => {
        const unsubscribe = form.store.subscribe(() => {
            const state = form.store.state;
            const subEntityFields = Object.keys(state.values).filter(key => 
                key.startsWith('__subEntity_') && key.endsWith('_dirty')
            );
            
            if (subEntityFields.length > 0) {
                console.log('Parent form detected sub-entity changes:', {
                    isDirty: state.isDirty,
                    subEntityFields,
                    subEntityValues: subEntityFields.map(field => ({
                        field,
                        value: state.values[field]
                    }))
                });
            }
        });
        
        return unsubscribe;
    }, [form]);

    // Note: Form reset is handled in the onSubmit function to avoid double resets

    // Toast notifications for save states
    useEffect(() => {
        if (updateEntityMutation.isSuccess || updateSubEntityMutation.isSuccess) {
            toast.success('Changes saved successfully');
        }
    }, [updateEntityMutation.isSuccess, updateSubEntityMutation.isSuccess]);

    useEffect(() => {
        if (updateEntityMutation.isError || updateSubEntityMutation.isError) {
            toast.error('Failed to save changes. Please try again.');
        }
    }, [updateEntityMutation.isError, updateSubEntityMutation.isError]);

    const [showMergeModal, setShowMergeModal] = useState(false);

    // Get the entity name from the last breadcrumb
    const entityName = result?.breadcrumbs?.[result.breadcrumbs.length - 1]?.name || currentEntity?.name || 'Unknown';

    const color = getEntityColor(entity_type_id);

    const sidePanels = result?.view?.primaryTabs?.filter(t => t.isSidePanel);

    const hideFirstColumnOnEntityTypes = ['reports', 'document-templates'];
    const hideFirstColumn = hideFirstColumnOnEntityTypes.includes(entity_type_id as string);

    const entityTypesToShowHouseholdMembers = ['households', 'volunteers', 'contacts', 'donors', 'clients'];
    let showHouseholdMembers = entityTypesToShowHouseholdMembers.includes(entity_type_id as string);

    // Get dirty state from form store
    const isDirty = useStore(form.store, state => state.isDirty);

    // Block navigation if form is dirty
    useBlocker({
        shouldBlockFn: () => {
            if (isDirty) {
                return !window.confirm('You have unsaved changes. Are you sure you want to leave?');
            }
            return false;
        },
        enableBeforeUnload: isDirty,
    });

    return (
        <>
            <SubEntityRegistrationContext.Provider value={{ registerSubEntityForm, unregisterSubEntityForm, storeSubEntityChanges, justSaved, updateInitialValues, registerUpdateCallback }}>
                <appFormContext.Provider value={form}>

                <form.Subscribe
                    selector={(state) => [state.canSubmit, state.isDirty]}
                    children={([canSubmit, isDirty]) => (
                        <form
                            onSubmit={(e) => {
                                e.preventDefault()
                                form.handleSubmit()
                            }}
                        >
                            <div className="absolute left-12 top-0 h-full min-h-full w-[calc(100%-50px)] ">
                                <header
                                    className={`z-50 sticky top-0  flex shrink-0 items-center gap-2 p-2  bg-white border-b-4 border-l-gray-200 border-r-gray-200 border-t-gray-200 border-t-2`}
                                    style={{
                                        borderBottomColor: color?.primaryColor
                                    }}
                                >
                                    <div className="flex flex-1 items-center gap-2 ">
                                        <Separator orientation="vertical" className="mr-2 h-4" />
                                        <EntityBreadcrumbs
                                            moduleDefinition={moduleDefinition}
                                            result={result}
                                            module_id={module_id}
                                            entity_type_id={entity_type_id}
                                        />
                                        <div className="flex items-center gap-2 ml-auto">
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="outline">
                                                        Actions <EllipsisVerticalIcon className="h-4 w-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent>
                                                    <DropdownMenuLabel>Quick Actions</DropdownMenuLabel>
                                                    <DropdownMenuSeparator />

                                                    {(moduleDefinition?.entityDefinitions?.find(ed => ed.id === entity_type_id)?.showMerge && moduleDefinition?.entityDefinitions?.find(ed => ed.id === entity_type_id)?.allowMerge) && <DropdownMenuItem
                                                        onClick={() => {
                                                            setShowMergeModal(true)
                                                        }}
                                                    >
                                                        Merge
                                                    </DropdownMenuItem>}

                                                </DropdownMenuContent>
                                            </DropdownMenu>


                                            <CreateButton />

                                            <Button
                                                className="ml-2 bg-green-600 hover:bg-green-700 disabled:bg-grey-100"
                                                onClick={() => {
                                                    console.log('=== SAVE BUTTON CLICKED ===');
                                                    form.handleSubmit()
                                                }}
                                                disabled={!canSubmit || !isDirty || updateEntityMutation.isPending || updateSubEntityMutation.isPending}
                                            >
                                                {(updateEntityMutation.isPending || updateSubEntityMutation.isPending) ? (
                                                    <>
                                                        <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />
                                                        Saving...
                                                    </>
                                                ) : (updateEntityMutation.isSuccess || updateSubEntityMutation.isSuccess) && !isDirty ? (
                                                    <>
                                                        <CheckIcon className="mr-2 h-4 w-4" />
                                                        Saved
                                                    </>
                                                ) : (
                                                    <>
                                                        <SaveIcon className="mr-2 h-4 w-4" />
                                                        Save
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="ml-auto px-3">
                                        {/* <NavActions /> */}
                                    </div>
                                </header>
                                <div className="flex flex-row h-full">
                                    {!hideFirstColumn && <div className="flex min-w-[300px] max-w-[300px] flex-col flex-1/3 p-3 border-r border-gray-200 bg-white shadow-xs overflow-scroll">
                                        <div className="flex-1">
                                            <ContactHeader
                                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                contact={currentEntity as any}
                                                entityType={moduleDefinition?.entityDefinitions?.find(ed => ed.id === entity_type_id)}
                                                entityId={entity_id}

                                            />
                                            <EntityHeader
                                                entityTypeId={entity_type_id}
                                                displayName={entityName}
                                                singularName={moduleDefinition?.entityDefinitions?.find(ed => ed.id === entity_type_id)?.name as string}
                                                // parentEntityTypeId={currentEntityDefinition?.parentEntityType} 
                                                // parentEntityId={currentEntityDefinition?.parentEntityId ?? undefined} 
                                                data={currentEntity?.entity?.data}
                                            />

                                            <Accordion type="multiple" defaultValue={['actions', "about", 'household-members']}>
                                                {(result?.view?.quickToDo && result.view.quickToDo.actions && result.view.quickToDo.actions.length > 0) && <AccordionItem value="actions" >
                                                    <AccordionTrigger>Recommended Actions</AccordionTrigger>
                                                    <AccordionContent >
                                                        <RecommendedActions quickToDo={result.view.quickToDo} />

                                                    </AccordionContent>
                                                </AccordionItem>}

                                                <AccordionItem value="about">
                                                    <AccordionTrigger>
                                                        <div className="flex items-center justify-between w-full pr-0">
                                                            <span>Overview</span>
                                                            <div
                                                                className="h-6 w-6 p-0 hover:bg-gray-100 text-gray-400 rounded cursor-pointer flex items-center justify-center"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    navigate({
                                                                        to: '/$module_id/$entity_type_id/$entity_id/sidebar-edit',
                                                                        params: { module_id, entity_type_id, entity_id }
                                                                    });
                                                                }}
                                                            >
                                                                <PencilIcon className="h-3 w-3" />
                                                            </div>
                                                        </div>
                                                    </AccordionTrigger>
                                                    <AccordionContent>
                                                        <OverviewPanel entity={currentEntity} entity_type_id={entity_type_id} />
                                                    </AccordionContent>
                                                </AccordionItem>

                                                {(showHouseholdMembers && currentEntity.householdId) && <AccordionItem value="household-members">
                                                    <AccordionTrigger>Household Members</AccordionTrigger>
                                                    <AccordionContent>
                                                        <HouseholdMembersPanel household_id={currentEntity.householdId} />
                                                    </AccordionContent>
                                                </AccordionItem>}

                                                {sidePanels?.map((sidePanel) => (
                                                    <AccordionItem key={sidePanel.name} value={sidePanel.name ?? ""}>
                                                        <AccordionTrigger>{sidePanel.name}</AccordionTrigger>
                                                        <AccordionContent>
                                                            {sidePanel.name}
                                                        </AccordionContent>
                                                    </AccordionItem>

                                                ))}
                                            </Accordion>

                                            <div style={{ height: '200px' }} />

                                        </div>

                                    </div>}
                                    <Outlet />
                                </div>

                            </div>

                            {/* Merge Contacts Modal */}
                            <MergeContactsModal
                                open={showMergeModal}
                                onOpenChange={setShowMergeModal}
                                collection={moduleDefinition?.entityDefinitions?.find(ed => ed.id === entity_type_id)?.id as string}
                                singularName={moduleDefinition?.entityDefinitions?.find(ed => ed.id === entity_type_id)?.singularName || 'Contact'}
                                currentContact={currentEntity}
                            />

                            <EntityActionsDropdown
                                moduleDefinition={moduleDefinition}
                                entity_type_id={entity_type_id}
                                entity_id={entity_id}
                                entityName={entityName}
                                module_id={module_id}
                                navigate={navigate}
                                setShowMergeModal={setShowMergeModal}
                            />

                            <CommandDialog open={visible} onOpenChange={setVisible}>
                                <CommandInput placeholder="Type a command or search..." />
                                <CommandList>
                                    <CommandEmpty>No results found.</CommandEmpty>
                                    <EntityCreateCommands
                                        entity_type_id={entity_type_id}
                                        entity_id={entity_id}
                                        module_id={module_id}
                                        subCollections={subCollections}
                                    />
                                </CommandList>
                            </CommandDialog>

                        </form>
                    )}
                />
            </appFormContext.Provider>
            </SubEntityRegistrationContext.Provider>
        </>
    )
}
